import { ApiError, buildUrl } from '@/shared/api/client';
import { useAuthStore } from '@/shared/auth/store';
import { toast } from 'sonner';

/**
 * Wrapper around the legacy SaleIT.Portal v1 RPC contract.
 *
 * Wire format (must match portal/src/app/common/common.service.js):
 *   POST  api/{Controller}/{Method}
 *   Content-Type: application/x-www-form-urlencoded; charset=utf-8
 *   Body: `=<urlencoded(JSON.stringify({
 *           lngKey,
 *           JsonData: JSON.stringify({ ...params, lngKey, UrlParams }),
 *           Offset,
 *           Fetch,
 *         }))>`
 *
 * Response: an array of records (`Array<Record<string, unknown>>`).  The first
 * record may carry a `Toast: { Type, Title, Message }` envelope which we
 * surface through `sonner`, exactly like the AngularJS portal does.
 *
 * Use this for everything that currently lives under SP_Common /
 * SP_VisitList / SP_Employees / etc.  When a module is fully ported we
 * usually replace the call site with a typed v2 endpoint and drop legacyRpc().
 */

export interface LegacyRpcOptions {
  /** Pagination offset (maps to T-SQL `OFFSET`).  Default 0. */
  offset?: number;
  /** Page size (maps to `FETCH NEXT`).  Default 10000 (effectively "all"). */
  fetch?: number;
  /** Override locale; defaults to the current i18next language. */
  lngKey?: string;
  /** Snapshot of UI URL/query params to forward as `UrlParams`. */
  urlParams?: Record<string, unknown> | null;
  /** Suppress automatic toast handling for the first row. */
  silent?: boolean;
}

interface LegacyToast {
  Type?: 'success' | 'info' | 'warning' | 'error';
  Title?: string;
  Message?: string;
}

interface LegacyRow {
  Toast?: LegacyToast;
  [k: string]: unknown;
}

function getCurrentLocale(): string {
  if (typeof window === 'undefined') return 'EN';
  const stored = localStorage.getItem('i18nextLng');
  return (stored ?? 'EN').toUpperCase();
}

/**
 * Detect whether a UTF-8 decoded string is actually cp1251→UTF-8 mojibake.
 *
 * The legacy backend reads cp1251 bytes from MSSQL, hands them off to .NET
 * `Encoding.Default` (cp1252 on the dev IIS) and re-encodes that string as
 * UTF-8 before sending the response.  The on-the-wire bytes are therefore
 * a valid UTF-8 sequence representing the *Cyrillic letters that visually
 * resemble cp1251 codepoints when interpreted as cp1252* (e.g. `Р°` for
 * what should be `а` — cp1251 0xE0 → cp1252 `Р°` → UTF-8 `0xD0 0xA0 0xD0 0xB0`).
 *
 * To recover the original Cyrillic we map each char back to its cp1252 byte
 * value and then decode the resulting byte stream as cp1251.  If the round
 * trip yields recognisably more Cyrillic letters and fewer suspect glyphs,
 * we use it; otherwise we fall back to the original string.  This avoids
 * corrupting genuinely-UTF-8 responses (e.g. v2 endpoints, JSON keys).
 */
function looksLikeMojibake(s: string): boolean {
  if (!s) return false;
  // Common cp1251→UTF-8 markers: capital Cyrillic letters used as filler
  // (`Р`, `С`, `Т`, `У`) followed by lowercase Cyrillic / punctuation.
  // The pattern below catches the typical run we see in
  // `Р°РєС‚РёРІРЅС‹` etc.  We only trigger the recovery path when there
  // are at least two such pairs — single matches are too noisy.
  let hits = 0;
  const re = /[\u0420\u0421\u0422\u0423][\u0080-\u00BF\u0400-\u04FF]/g;
  while (re.exec(s) !== null) {
    if (++hits >= 2) return true;
  }
  return false;
}

/**
 * Recover original cp1251 text from a string that was double-encoded as
 * `cp1251 bytes → cp1252 string → utf-8`.  Returns the recovered string
 * if it produced cleaner Cyrillic; otherwise returns the input unchanged.
 */
function recoverFromMojibake(s: string): string {
  // Map every char to its cp1252 byte value (0x00..0xFF).  Chars outside
  // that range are unrepresentable in cp1252 — bail out and return input.
  const bytes = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) {
    const code = s.charCodeAt(i);
    if (code > 0xff) return s;
    bytes[i] = code;
  }
  try {
    const recovered = new TextDecoder('windows-1251').decode(bytes);
    // Sanity check: the recovered string should not itself look like
    // mojibake (it may, if the source really was cp1252 garbage).
    return looksLikeMojibake(recovered) ? s : recovered;
  } catch {
    return s;
  }
}

/**
 * Decode an IIS response body using the declared charset, with a fallback
 * to windows-1251 mojibake recovery when the server forgets to set one.
 *
 *   1. If Content-Type carries `charset=…` we honour it (TextDecoder).
 *   2. Otherwise we decode as UTF-8 first (the modern path).
 *   3. If the UTF-8 result contains the cp1251 mojibake signature, we
 *      run the cp1252→cp1251 round trip and prefer that result.
 */
async function decodeResponseBody(res: Response): Promise<string> {
  const ct = res.headers.get('Content-Type') ?? '';
  const charsetMatch = /charset=([^;]+)/i.exec(ct);
  const declared = charsetMatch?.[1]?.trim().toLowerCase();
  const buf = await res.arrayBuffer();
  if (buf.byteLength === 0) return '';

  // Honour explicit non-UTF-8 charsets directly (rare on this backend, but
  // possible if someone sets `<globalization responseEncoding="windows-1251"/>`).
  if (declared && declared !== 'utf-8' && declared !== 'utf8') {
    try {
      return new TextDecoder(declared).decode(buf);
    } catch {
      /* fall through to UTF-8 + mojibake recovery */
    }
  }

  const utf8 = new TextDecoder('utf-8').decode(buf);
  if (looksLikeMojibake(utf8)) return recoverFromMojibake(utf8);
  return utf8;
}

function showToastFromRow(row: LegacyRow): void {
  if (!row?.Toast) return;
  const t = row.Toast;
  const title = t.Title ?? '';
  let message = '';
  try {
    const parsed = t.Message ? (JSON.parse(t.Message) as Array<{ value: string }>) : [];
    message = parsed.map((o) => o.value).join('');
  } catch {
    message = t.Message ?? '';
  }
  switch (t.Type) {
    case 'error':
      toast.error(title || message, { description: title ? message : undefined });
      break;
    case 'warning':
      toast.warning(title || message, { description: title ? message : undefined });
      break;
    case 'success':
      toast.success(title || message, { description: title ? message : undefined });
      break;
    default:
      toast.info(title || message, { description: title ? message : undefined });
  }
}

export async function legacyRpc<T extends LegacyRow = LegacyRow>(
  controller: string,
  method: string,
  params: Record<string, unknown> = {},
  opts: LegacyRpcOptions = {}
): Promise<T[]> {
  const { offset = 0, fetch: pageSize = 10000, urlParams = null, silent = false } = opts;
  const lngKey = (opts.lngKey ?? getCurrentLocale()).toUpperCase();

  const innerJsonData = JSON.stringify({
    ...params,
    lngKey,
    UrlParams: urlParams ? JSON.stringify(urlParams) : null,
  });

  const formPayload = JSON.stringify({
    lngKey,
    JsonData: innerJsonData,
    Offset: offset,
    Fetch: pageSize,
  });

  const headers = new Headers({
    'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8',
    Accept: 'application/json',
  });

  const res = await fetch(buildUrl(`/api/${controller}/${method}`), {
    method: 'POST',
    credentials: 'include',
    headers,
    body: `=${encodeURIComponent(formPayload)}`,
  });

  if (res.status === 401) {
    useAuthStore.getState().clear();
    throw new ApiError(401, 'Unauthorized');
  }

  if (!res.ok) {
    let body: unknown;
    try {
      body = await decodeResponseBody(res.clone());
      try {
        body = JSON.parse(body as string);
      } catch {
        /* leave as text */
      }
    } catch {
      body = undefined;
    }
    throw new ApiError(res.status, `legacy ${controller}/${method} failed`, body);
  }

  const text = await decodeResponseBody(res);
  const data = (text ? (JSON.parse(text) as T[]) : []) as T[];

  if (!silent && Array.isArray(data) && data.length > 0) {
    showToastFromRow(data[0]);
  }

  return data;
}

/**
 * Variant of `legacyRpc` that returns the raw response as a Blob.  Used by
 * binary endpoints — currently only the photo-viewer download flow which
 * pulls `archive/.../photo.jpg` straight from the backend after looking up
 * the original filename via `SP_Common/getPhotoInfo`.
 *
 * Note: SP_PhotoViewer/getPhoto{ZIP,PDF,...} bulk exports return arraybuffer
 * directly from the SP; we'll wire those up the same way once Wave 1C
 * (multi-select export toolbar) lands.
 */
export async function legacyBlob(
  /** Absolute URL OR backend-relative path (e.g. `archive/...`). */
  url: string
): Promise<Blob> {
  const headers = new Headers({ Accept: '*/*' });

  const res = await fetch(url.startsWith('http') ? url : buildUrl(url), {
    method: 'GET',
    credentials: 'include',
    headers,
  });

  if (res.status === 401) {
    useAuthStore.getState().clear();
    throw new ApiError(401, 'Unauthorized');
  }
  if (!res.ok) {
    throw new ApiError(res.status, `legacyBlob ${url} failed`);
  }
  return res.blob();
}
